import React, { useState, useEffect } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import Moment from "react-moment";
import {
  Row,
  Col,
  Button,
  Modal,
  Form,
  Tooltip,
  OverlayTrigger,
  Alert,
} from "react-bootstrap";

import API from "../Utility";
import Search from "../Search";
import Table, { DefaultTableSettings } from '../Utility/Table';
import variables from "../../variables.scss";
import useElementOnScreen from "../Utility/useElementOnScreen";
import TeamMember from "./TeamMember";
import InviteTeamMemberFormModal from "./InviteTeamMemberFormModal";
import ConfirmationModal from "../ConfirmationModal";
import ErrorModal from "../ErrorModal";

import "./TeamView.scss";

const TeamView = () => {
  const { project } = useParams();
  const teamUrl = `/admin/${project}/team`;
  const [teamData, setTeamData] = useState([]);
  const [filter, setFilter] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResultsCount, setTotalResultsCount] = useState(0);
  const [itemsRendered, setItemsRendered] = useState([]);

  let pageSize = 30;
  let loadIncrement = 20;
  const [itemsToRenderPerPageBeforeLoadMore, setItemsToRenderPerPageBeforeLoadMore] = useState(loadIncrement);
  const [page, setPage] = useState(1);
  const [loadMore, setLoadMore] = useState(true);
  const [editSelectedUser, setEditSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('invite');

  const [showInviteTeamMemberFormModal, setShowInviteTeamMemberFormModal] = useState(false);
  const [showConfirmDeleteTeamMember, setShowConfirmDeleteTeamMember] = useState(false);
  const [deleteUserError, setDeleteUserError] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  function roleDisplayText(role) {
    switch (role) {
      case 'admin':
        role = 'Project Admin';
        return role;
      // break;
      case 'pageAdmin':
        role = 'Lesson Admin';
        return role;
      // break;
      case 'pageDeveloper':
        role = 'Lesson Developer';
        return role;
      // break;
      case 'contentCreator':
        role = 'Content Creator';
        return role;
      case 'workflowManager':
        role = 'Workflow Manager';
        return role;
      case 'workflowUser':
        role = 'Workflow User';
        return role;
      // break;
      default:
        role = '-';
        return role;
    }
  }
  let columns = [
    {
      dataField: "displayName",
      text: "Display Name",
      colSpan: 2
    },
    {
      dataField: "email",
      text: "Email",
      colSpan: 3,
      lineBreak: 'anywhere'
    },
    {
      dataField: "roles",
      text: 'Role(s)',
      colSpan: 2,
      formatter: function callback(cell, row) {
        let roles = [];
        if (cell?.[0]) {
          roles = cell.sort((a, b) => {
            if (roleDisplayText(a) < roleDisplayText(b)) { return -1; }
            if (roleDisplayText(a) > roleDisplayText(b)) { return 1; }
            return 0;
          });
        }
        return (cell?.[0] ?
          <>
            {cell.length > 2 ? (
              <OverlayTrigger
                placement={'top'}
                overlay={
                  <Tooltip id="delete-tooltip-disabled">
                    <p>{roles.map((x, ri) => {
                      return (
                        <span key={`tooltip-role-${ri}`}>
                          {roleDisplayText(x)}<br></br>
                        </span>
                      );
                    })}</p>
                  </Tooltip>
                }
              >
                <div>
                  {roles.map((x, indx) => {
                    if (indx < 1) {
                      return (<span key={`role-${indx}`}>{roleDisplayText(x) + (indx < cell.length - 1 ? ', ' : '')}</span>);
                    } else if (indx === 1) {
                      return (<span key={`role-${indx}`}>{roleDisplayText(x) + (indx < cell.length - 1 ? '... ' : '')}</span>);
                    }
                  })}
                </div>
              </OverlayTrigger>
            ) : (<div>
              {roles.map((x, indx) => {
                return (<span key={`role-${indx}`}>{roleDisplayText(x) + (indx < cell.length - 1 ? ', ' : '')}</span>);
              })}
            </div>)}
          </>
          :
          <span></span>
        );
      }
    }, {
      dataField: "status",
      text: "Last Signed In",
      colSpan: 1,
      formatter: function callback(cell, row) {
        if (row?.userToken?.modified || row?.userToken?.created) {
          return <Moment format="DD MMM 'YY">{row.userToken.modified || row.userToken.created}</Moment>;
        } else {
          return <span>Not Yet</span>;
        }
      }
    }
  ];

  async function getTeamData({
    query = null,
    url = teamUrl,
    params = filter,
  }) {
    if (!loadMore) {
      setPage(1); // already in params?
      setTeamData([]);
      setItemsRendered([]);
      setItemsToRenderPerPageBeforeLoadMore(loadIncrement);
      // setPage(1);
    }
    setIsLoading(true);
    params.nameMatch = query;
    setFilter(params);
    try {
      const { filteredResults, totalResultsCount } = await API.get({
        url: url,
        params: params,
      });
      if (loadMore) {
        let newResults = [...teamData, ...filteredResults];
        setTeamData(newResults);
      } else {
        setTeamData(filteredResults);
      }
      setTotalResultsCount(totalResultsCount);
      setIsLoading(false);
      setLoadMore(false);
    } catch(err) {
      console.error(err);
      setTeamData([]);
      setIsLoading(false);
      setLoadMore(false);
      setTotalResultsCount(0);
    }
  }
  async function initializeData(resetPaging = false) {
    await getTeamData({
      params: {
        page: resetPaging ? 1 : page,
        pageSize: pageSize,
      },
      query: searchText,
    });
  }
  useEffect(() => {
    initializeData();
  }, []);
  useEffect(() => {
    if (loadMore) {
      initializeData();
    }
  }, [loadMore]);

  useEffect(() => {
    if (itemsRendered.length >= itemsToRenderPerPageBeforeLoadMore) {
      setItemsToRenderPerPageBeforeLoadMore(itlpp => itlpp + (loadIncrement + (pageSize - loadIncrement)));
      // console.log("Load next page...");
      let newPage = page + 1;
      setPage(newPage);
      setLoadMore(true);
    }
  }, [itemsRendered]);

  const [containerRef, isVisible] = useElementOnScreen({
    root: null,
    rootMargin: "0px",
    threshold: 1.0
  });

  const onSuccessInviteTeamMemberFormModal = (data) => {
    setShowInviteTeamMemberFormModal(false);
    let resetPaging = true;
    initializeData(resetPaging);
  };

  useEffect(() => {
    if (editSelectedUser) {
      setShowInviteTeamMemberFormModal(true);
    }
  }, [editSelectedUser]);

  const onDeleteTeamMember = (user) => {
    setUserToDelete(user);
    setShowConfirmDeleteTeamMember(true);
  };

  const deleteTeamMember = async(e, confirmed) => {
    if (confirmed) {
      const apiCallConfig = {
        url: `/admin/${project}/team/member/${userToDelete._id}`,
        data: JSON.stringify(userToDelete),
      };
      const apiCallAsync = async () => {
        try {
          const apiCallRes = await API.delete(apiCallConfig);
          if (apiCallRes?.message !== 'success') {
            throw new Error(apiCallRes);
          } else {
            setShowConfirmDeleteTeamMember(false);
            let resetPaging = true;
            initializeData(resetPaging);
          }
        } catch (err) {
          console.error(err);
          setDeleteUserError('Something went wrong. Please refresh and try again.');
        }
      };
      apiCallAsync();
    } else {
      setUserToDelete(null);
      setShowConfirmDeleteTeamMember(false);
    }
  };

  return (
    <div className="team-view">
      <Row>
        <Col lg={6}>
          <h2>Team</h2>
        </Col>
        <Col lg={6}>
          <div
            className="team-view__search-form flex flex-row"
            style={{ marginLeft: "5px" }}
          >
            <Search
              placeholder="Search"
              labelKey="username"
              options={teamData}
              isLoading={isLoading}
              isTypeAhead={false}
              minLength={3}
              delay={450}
              callback={(text) => {
                if (text.length > 0 && text.length < 3) {
                  return;
                }
                setCurrentPage(1);
                let currentParams = filter;
                currentParams.page = 1;
                setSearchText(text);
                return getTeamData({ query: text, params: currentParams });
              }}
            />
            <Button
              onClick={() => {
                setEditSelectedUser(null);
                setShowInviteTeamMemberFormModal(true);
                setModalMode('invite');
              }}
              variant="outline-primary"
            >
              <span style={{ verticalAlign: "middle", fontSize: '30px', lineHeight: '10px', marginTop: '-6px' }}>+</span>
              <span style={{ verticalAlign: "middle" }}> Invite</span>
            </Button>
          </div>
        </Col>
      </Row>
      { !isLoading && teamData.length === 0 &&
        <div>
          <span>No results</span>
        </div>
      }
      <div className="team-view__table">
        { teamData.length > 0 &&
        <div>
          <Row className="team-view__header-row">
            { columns.map((column, index) => {
              return (
                <Col key={`column-${index}`} className="team-view__col" xs={column.colSpan}>
                  {column.text}
                </Col>
              );
            })}
            <Col className="team-view__col" xs="4"></Col>
          </Row>
          { teamData?.[0] && teamData.map((row, index) => {
            return (
              <TeamMember
                key={`team-row-${index}`}
                index={index}
                user={row}
                columns={columns}
                itemsRendered={itemsRendered}
                setItemsRendered={setItemsRendered}
                onEditSelected={() => {
                  setEditSelectedUser(row);
                }}
                setModalMode={setModalMode}
                onDeleteTeamMember={onDeleteTeamMember}
              />
            );
          })}
        </div>
        }
        { isLoading &&
        <div className='team-view__loader'>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
        }
      </div>

      <InviteTeamMemberFormModal
        show={showInviteTeamMemberFormModal}
        selectedUser={editSelectedUser}
        onCancel={() => {
          setEditSelectedUser(null);
          setShowInviteTeamMemberFormModal(false);
        }}
        onSuccess={onSuccessInviteTeamMemberFormModal}
        modalMode={modalMode}
      />
      <ConfirmationModal
        title={`Delete User`}
        body={`This will remove the user from the team as well as all workflow assignee roles and can't be undone. Do you want to proceed?`}
        show={showConfirmDeleteTeamMember}
        confirmtext="OK"
        onHide={deleteTeamMember}
      />
      <ErrorModal
        show={deleteUserError}
        onHide={() => setDeleteUserError(null)}
        title="Error"
        body={deleteUserError}
        confirmtext="OK"
      />
    </div>
  );
};

export default TeamView;
